import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import ActionButton from "../ActionButton";

const ToastContext = createContext(null);

const toastStyles = {
  success: {
    icon: CheckCircle2,
    className: "border-green-200 bg-green-50 text-green-800",
    iconClassName: "text-green-600",
  },
  error: {
    icon: AlertCircle,
    className: "border-red-200 bg-red-50 text-red-800",
    iconClassName: "text-red-600",
  },
  info: {
    icon: AlertCircle,
    className: "border-blue-200 bg-blue-50 text-blue-800",
    iconClassName: "text-blue-600",
  },
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    ({ type = "info", message, duration = 3500 }) => {
      if (!message) return;

      const id = `${Date.now()}-${Math.random()}`;
      setToasts((current) => [...current, { id, type, message }]);

      window.setTimeout(() => removeToast(id), duration);
    },
    [removeToast]
  );

  const value = useMemo(
    () => ({
      showToast,
      success: (message, options) => showToast({ ...options, type: "success", message }),
      error: (message, options) => showToast({ ...options, type: "error", message }),
      info: (message, options) => showToast({ ...options, type: "info", message }),
    }),
    [showToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-3 right-3 z-[1200] flex w-[calc(100vw-1.5rem)] max-w-sm flex-col gap-3 sm:bottom-4 sm:right-4 sm:w-full lg:bottom-5 lg:right-5">
        {toasts.map((toast) => {
          const config = toastStyles[toast.type] || toastStyles.info;
          const Icon = config.icon;

          return (
            <div
              key={toast.id}
              className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm font-medium shadow-sm ${config.className}`}
              role="status"
            >
              <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${config.iconClassName}`} />
              <p className="min-w-0 flex-1 break-words">{toast.message}</p>
              <ActionButton
                action="cancel"
                iconOnly
                variant="ghost"
                aria-label="Dismiss notification"
                className="h-6 w-6 shrink-0"
                onClick={() => removeToast(toast.id)}
              />
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within ToastProvider.");
  }

  return context;
};
